import type { ClientError, NetworkError, ServerError } from '@axios-extensions/plugins-api'
import type { AxiosInstance, AxiosResponse } from 'axios'
import type { AuthResponseV2, LoginRequestInterface, RefreshRequestInterface } from './Interface'

export interface AuthPluginConfigInterface {
  path: AuthPath
  axios: AxiosInstance
  onAuthenticateSuccess?: (response: ResponseTypeBasedOnPath<AuthPath>, input: RequestTypeBasedOnPath<AuthPath>) => Promise<void>
  onAuthenticateFailed?: (error: ClientError | ServerError | NetworkError) => Promise<void>
}

export enum AuthPath {
  JSON_LOGIN_V2 = '/api/auth/login',
  JSON_REFRESH_V2 = '/api/auth/refresh',
}

export type ResponseTypeBasedOnPath<T extends AuthPath> = T extends AuthPath.JSON_LOGIN_V2
  ? AuthResponseV2
  : T extends AuthPath.JSON_REFRESH_V2
    ? AuthResponseV2
    : never

export type RequestTypeBasedOnPath<T extends AuthPath> = T extends AuthPath.JSON_LOGIN_V2
  ? LoginRequestInterface
  : T extends AuthPath.JSON_REFRESH_V2
    ? RefreshRequestInterface
    : never

export function createAuthenticatePlugin(
  config: AuthPluginConfigInterface,
) {
  const currentPromise: Record<
    AuthPath,
        null | Promise<ResponseTypeBasedOnPath<typeof config.path>>
  > = {
    [AuthPath.JSON_LOGIN_V2]: null,
    [AuthPath.JSON_REFRESH_V2]: null,
  }

  function clearCurrentPromise() {
    currentPromise[config.path] = null
  }

  async function authenticate(
    input: RequestTypeBasedOnPath<typeof config.path>,
  ): Promise<ResponseTypeBasedOnPath<typeof config.path>> {
    let promise = currentPromise[config.path]
    if (!promise) {
      promise = config.axios.post<ResponseTypeBasedOnPath<typeof config.path>>(config.path, input)
        .then(async (r: AxiosResponse) => {
          if (config.onAuthenticateSuccess) {
            await config.onAuthenticateSuccess(r.data, input)
          }

          return <AuthResponseV2>r.data
        })
        .catch(async (e: ClientError | ServerError | NetworkError) => {
          if (config.onAuthenticateFailed) {
            await config.onAuthenticateFailed(e)
          }

          throw e
        })
        .finally(clearCurrentPromise)

      currentPromise[config.path] = promise
    }

    await promise

    return promise
  }

  return { authenticate }
}
